import React from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { EllipsisVerticalIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { Spinner } from '@oysterjs/ui/spinner';
import { Button } from '@oysterjs/ui/button';
import { Badge } from '@oysterjs/ui/badge';
import { Action, Resource } from '~/api/graphql';
import { usePermissions } from '~/components/permissions';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Textarea } from '@oysterjs/ui/textarea';
import {
  type AgencyUserFieldsFragment,
  type TeamFieldsFragment,
  useArchiveTeamMutation,
  useCreateTeamMutation,
  useGetTeamsQuery,
  useGetAgencyUsersQuery,
  useUpdateTeamMutation
} from '~/api/graphql';
import { EnterpriseUpgradeNotice } from '~/components/settings/enterprise-upgrade-notice';
import { useTeamManagementEntitlement } from '~/components/visibility/use-visibility-entitlement';

type Team = TeamFieldsFragment;

type AgencyUserOption = { id: string; name: string; email: string };

/**
 * Searchable multi-select for picking team members. Built on Headless UI's
 * multi-mode Combobox so it scales to 50+ agency users without becoming a
 * flat scrolling brick. Selected users appear as removable chips above the
 * input; typing in the input filters the dropdown.
 */
const TeamMemberPicker: React.FC<{
  agencyUsers: AgencyUserOption[];
  selectedIds: string[];
  onChange: (next: string[]) => void;
}> = ({ agencyUsers, selectedIds, onChange }) => {
  const [query, setQuery] = React.useState('');
  const usersById = React.useMemo(() => new Map(agencyUsers.map((u) => [u.id, u])), [agencyUsers]);
  const selected = React.useMemo(
    () =>
      selectedIds.map((id) => usersById.get(id)).filter((u): u is AgencyUserOption => Boolean(u)),
    [selectedIds, usersById]
  );

  const q = query.trim().toLowerCase();
  const filtered = q
    ? agencyUsers.filter(
        (u) => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
      )
    : agencyUsers;

  const removeMember = (id: string) => onChange(selectedIds.filter((x) => x !== id));

  return (
    <div className="space-y-2">
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((u) => (
            <span
              key={u.id}
              className="inline-flex items-center gap-1 rounded-full bg-primary-100 dark:bg-primary-500/20 px-2 py-0.5 text-xs text-primary-900 dark:text-primary-200"
            >
              <span className="truncate max-w-[12rem]">{u.name || u.email}</span>
              <button
                type="button"
                aria-label={`Remove ${u.name || u.email}`}
                onClick={() => removeMember(u.id)}
                className="rounded-full p-0.5 hover:bg-primary-200/70 dark:hover:bg-primary-500/25"
              >
                <XMarkIcon className="size-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <Headless.Combobox
        multiple
        value={selectedIds}
        onChange={(next: string[]) => {
          onChange(next);
          setQuery('');
        }}
      >
        <div className="relative">
          <Headless.ComboboxInput
            aria-label="Add team members"
            placeholder={selected.length === 0 ? 'Search agency users…' : 'Add another…'}
            displayValue={() => query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            className={clsx(
              'block w-full rounded-md border border-neutral-300 bg-white py-1.5 pl-3 pr-8 text-sm',
              'placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500',
              'dark:border-white/10 dark:bg-white/5 dark:text-white'
            )}
          />
          <Headless.ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2 text-neutral-500 dark:text-neutral-400">
            <svg className="size-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M5.75 10.75L8 13L10.25 10.75"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                stroke="currentColor"
              />
              <path
                d="M10.25 5.25L8 3L5.75 5.25"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                stroke="currentColor"
              />
            </svg>
          </Headless.ComboboxButton>
          <Headless.ComboboxOptions
            anchor="bottom start"
            className="z-[200] mt-1 max-h-56 w-[var(--input-width)] overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-neutral-800 dark:ring-white/10 [--input-width:theme(spacing.96)]"
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-neutral-500 dark:text-neutral-400">
                No agency users match.
              </div>
            ) : (
              filtered.map((u) => {
                const isSelected = selectedIds.includes(u.id);
                return (
                  <Headless.ComboboxOption
                    key={u.id}
                    value={u.id}
                    className={clsx(
                      'flex cursor-default select-none items-center gap-2 px-3 py-1.5',
                      'data-[focus]:bg-primary-50 dark:data-[focus]:bg-primary-500/10 data-[focus]:text-primary-900 dark:data-[focus]:text-primary-200'
                    )}
                  >
                    <span className="flex size-4 items-center justify-center">
                      {isSelected ? (
                        <CheckIcon className="size-4 text-primary-600 dark:text-primary-400" />
                      ) : null}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-neutral-900 dark:text-white">
                        {u.name || u.email}
                      </div>
                      {u.name && u.email ? (
                        <div className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                          {u.email}
                        </div>
                      ) : null}
                    </div>
                  </Headless.ComboboxOption>
                );
              })
            )}
          </Headless.ComboboxOptions>
        </div>
      </Headless.Combobox>
    </div>
  );
};

// Pull initials from the first two name tokens that start with a letter or
// number. Skips parenthetical suffixes like "Carol (Team share)" -> "C(" by
// requiring the first character to be a letter; legacy test users with
// suffixes still produce a clean "CA" / "BO" / "DA" pair.
const getInitials = (name: string): string => {
  const tokens = name
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => /^[A-Za-z0-9]/.test(t));
  return tokens
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
};

interface TeamFormState {
  name: string;
  description: string;
  memberUserIds: string[];
}

const emptyForm: TeamFormState = { name: '', description: '', memberUserIds: [] };

const formFromTeam = (team: Team): TeamFormState => ({
  name: team.name,
  description: team.description ?? '',
  memberUserIds: team.members.map((m) => m.user.id)
});

interface TeamFormDialogProps {
  open: boolean;
  onClose: () => void;
  initial: TeamFormState;
  agencyUsers: AgencyUserFieldsFragment[];
  title: string;
  description: string;
  submitLabel: string;
  loading: boolean;
  error?: string;
  onSubmit: (form: TeamFormState) => void;
}

const TeamFormDialog: React.FC<TeamFormDialogProps> = ({
  open,
  onClose,
  initial,
  agencyUsers,
  title,
  description,
  submitLabel,
  loading,
  error,
  onSubmit
}) => {
  const [form, setForm] = React.useState<TeamFormState>(initial);

  React.useEffect(() => {
    if (open) {
      setForm(initial);
    }
  }, [open, initial.name, initial.description, initial.memberUserIds.join('|')]);

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogTitle>{title}</DialogTitle>
      <DialogDescription>{description}</DialogDescription>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(form);
        }}
      >
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Team name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
                  placeholder="e.g. Commercial Lines"
                  required
                />
              </Field>
              <Field>
                <Label>Description (optional)</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.currentTarget.value })}
                  rows={3}
                  placeholder="What this team does or owns."
                />
              </Field>
              <Field>
                <Label>Members</Label>
                <Description>
                  Select agency users to include in this team. Membership controls which SHARED
                  workspaces, presets, templates, and forms each user can see.
                </Description>
                <TeamMemberPicker
                  agencyUsers={agencyUsers}
                  selectedIds={form.memberUserIds}
                  onChange={(memberUserIds) => setForm({ ...form, memberUserIds })}
                />
              </Field>
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" color="sky" loading={loading} disabled={!form.name.trim()}>
            {submitLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const TeamRow: React.FC<{
  team: Team;
  canUpdate: boolean;
  onEdit: () => void;
  onArchive: () => void;
}> = ({ team, canUpdate, onEdit, onArchive }) => {
  // The admin is the team's creator. Showing only the admin avatar keeps the
  // row layout stable for 50+ member teams; the full roster is revealed on
  // hover via a native tooltip so users still have a way to inspect it.
  const adminName = team.createdBy.name || team.createdBy.email;
  const memberNames = team.members.map((m) => m.user.name || m.user.email);
  const memberTooltip = memberNames.length
    ? `Members: ${memberNames.join(', ')}`
    : 'No members yet';
  return (
    <li className="flex justify-between px-4 py-4 first:pt-0 last:pb-0 sm:px-6">
      <div className="flex min-w-0 gap-x-4">
        <div
          className="flex size-12 flex-none items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-700 text-lg font-semibold text-neutral-700 dark:text-neutral-300"
          title={`Admin: ${adminName}`}
        >
          {getInitials(adminName)}
        </div>
        <div className="min-w-0 flex-auto">
          <p className="text-sm/6 font-semibold text-neutral-900 dark:text-white">
            {team.name}
            <span className="ml-2 cursor-help" title={memberTooltip}>
              <Badge color="sky">
                {team.memberCount} {team.memberCount === 1 ? 'member' : 'members'}
              </Badge>
            </span>
          </p>
          <p className="flex text-xs/5 text-neutral-500 dark:text-neutral-400">
            Admin · {adminName}
            {team.description ? <> · {team.description}</> : null}
          </p>
        </div>
      </div>
      {canUpdate ? (
        <div className="flex shrink-0 items-center gap-x-6">
          <Headless.Menu as="div" className="relative flex-none">
            <Headless.MenuButton className="-m-2.5 block p-2.5 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white">
              <span className="sr-only">Open options for {team.name}</span>
              <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
            </Headless.MenuButton>
            <Headless.MenuItems
              transition
              className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white dark:bg-neutral-800 py-2 shadow-lg ring-1 ring-neutral-900/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <Headless.MenuItem>
                <span
                  role="button"
                  onClick={onEdit}
                  className="block px-3 py-1 text-sm/6 text-neutral-900 dark:text-white data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5 data-[focus]:outline-none"
                >
                  Edit<span className="sr-only">, {team.name}</span>
                </span>
              </Headless.MenuItem>
              <Headless.MenuItem>
                <span
                  role="button"
                  onClick={onArchive}
                  className="block px-3 py-1 text-sm/6 text-neutral-900 dark:text-white data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5 data-[focus]:outline-none"
                >
                  Archive<span className="sr-only">, {team.name}</span>
                </span>
              </Headless.MenuItem>
            </Headless.MenuItems>
          </Headless.Menu>
        </div>
      ) : null}
    </li>
  );
};

export const SettingsTeamsPage: React.FC = () => {
  const teamManagement = useTeamManagementEntitlement();
  const { hasPermission } = usePermissions();
  // Show the page to anyone with TEAM:LIST (the route guard already enforces
  // that). Only surface mutation affordances (Create / Edit / Archive) when
  // the caller can actually perform them — non-admins on Enterprise can read
  // the team list but cannot mutate it, and showing buttons that throw on
  // submit is worse than not showing them at all.
  const canCreate = hasPermission(Resource.Team, Action.Create);
  const canUpdate = hasPermission(Resource.Team, Action.Update);

  const { data, loading, error, refetch } = useGetTeamsQuery({
    skip: !teamManagement,
    fetchPolicy: 'cache-and-network'
  });
  const { data: usersData } = useGetAgencyUsersQuery({
    skip: !teamManagement,
    fetchPolicy: 'cache-first'
  });
  const [createTeamMutation, createState] = useCreateTeamMutation();
  const [updateTeamMutation, updateState] = useUpdateTeamMutation();
  const [archiveTeamMutation] = useArchiveTeamMutation();

  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Team | null>(null);
  const [archiving, setArchiving] = React.useState<Team | null>(null);
  const [archiveLoading, setArchiveLoading] = React.useState(false);
  const [archiveError, setArchiveError] = React.useState<string>();

  if (!teamManagement) {
    return (
      <EnterpriseUpgradeNotice
        title="Teams"
        icon={UserGroupIcon}
        heading="Teams are available on the Enterprise plan"
        documentationUrl="https://docs.getcara.ai/admin/profiles"
        canContactSales={canCreate}
      >
        Group agency users into teams to share workspaces, presets, templates, and forms with the
        right people.
      </EnterpriseUpgradeNotice>
    );
  }

  const agencyUsers = usersData?.agencyUsers ?? [];
  const teams = (data?.teams ?? []).filter((t) => !t.archivedAt);

  return (
    <div className="space-y-4">
      <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="m-0 p-0 text-lg font-medium text-neutral-900 dark:text-white">
                Teams
              </h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                Group agency users into teams to scope shared visibility on workspaces, presets, and
                templates.
              </p>
            </div>
            {canCreate ? (
              <Button color="sky" onClick={() => setCreateOpen(true)}>
                Create team
              </Button>
            ) : null}
          </div>
        </div>
        <div className="py-5">
          {error ? (
            <div className="px-4 py-2 text-sm text-red-900 dark:text-red-200 sm:px-6">
              Failed to load teams: {error.message}
            </div>
          ) : null}

          {loading && !data ? (
            <div className="flex justify-center py-4">
              <Spinner size="size-6" color="neutral" />
            </div>
          ) : teams.length === 0 ? (
            <p className="px-4 py-4 text-center text-sm text-neutral-500 dark:text-neutral-400 sm:px-6">
              {canCreate
                ? 'No teams yet. Create your first team to start scoping shared visibility.'
                : 'No teams have been set up yet. Ask an agency admin to create one.'}
            </p>
          ) : (
            <ul role="list" className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {teams.map((team) => (
                <TeamRow
                  key={team.id}
                  team={team}
                  canUpdate={canUpdate}
                  onEdit={() => setEditing(team)}
                  onArchive={() => {
                    setArchiveError(undefined);
                    setArchiving(team);
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      <TeamFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        initial={emptyForm}
        agencyUsers={agencyUsers}
        title="Create team"
        description="Teams group agency users together for SHARED visibility on workspaces, presets, templates, and forms."
        submitLabel="Create team"
        loading={createState.loading}
        error={createState.error?.message}
        onSubmit={async (form) => {
          try {
            await createTeamMutation({
              variables: {
                input: {
                  name: form.name.trim(),
                  description: form.description.trim() || null,
                  memberUserIds: form.memberUserIds
                }
              }
            });
            setCreateOpen(false);
            await refetch();
          } catch {
            // Error surfaced via createState.error
          }
        }}
      />

      <TeamFormDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        initial={editing ? formFromTeam(editing) : emptyForm}
        agencyUsers={agencyUsers}
        title="Edit team"
        description="Update team metadata and replace its full member list."
        submitLabel="Save changes"
        loading={updateState.loading}
        error={updateState.error?.message}
        onSubmit={async (form) => {
          if (!editing) return;
          try {
            await updateTeamMutation({
              variables: {
                id: editing.id,
                input: {
                  name: form.name.trim(),
                  description: form.description.trim() || null,
                  memberUserIds: form.memberUserIds
                }
              }
            });
            setEditing(null);
            await refetch();
          } catch {
            // Error surfaced via updateState.error
          }
        }}
      />

      <Dialog open={!!archiving} onClose={() => setArchiving(null)}>
        <DialogTitle>Archive team</DialogTitle>
        <DialogDescription>
          {archiving ? (
            <>
              Are you sure you want to archive <strong>{archiving.name}</strong>? Workspaces and
              other objects shared with this team will no longer be visible to its members through
              the team. Existing access lists are preserved but the archived team produces no new
              visibility matches.
            </>
          ) : null}
        </DialogDescription>
        {archiveError ? (
          <DialogBody>
            <ErrorMessage>{archiveError}</ErrorMessage>
          </DialogBody>
        ) : null}
        <DialogActions>
          <Button color="white" onClick={() => setArchiving(null)} disabled={archiveLoading}>
            Cancel
          </Button>
          <Button
            color="sky"
            loading={archiveLoading}
            onClick={async () => {
              if (!archiving) return;
              setArchiveLoading(true);
              setArchiveError(undefined);
              try {
                await archiveTeamMutation({ variables: { id: archiving.id } });
                setArchiving(null);
                await refetch();
              } catch (err) {
                setArchiveError(err instanceof Error ? err.message : 'Failed to archive team');
              } finally {
                setArchiveLoading(false);
              }
            }}
          >
            Archive team
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

interface MyTeamsSectionProps {
  /** Optional override; primarily for tests. Production reads from useGetMyTeamsQuery. */
  teams?: { id: string; name: string }[];
}

/**
 * Profile-page section listing the teams the current user is a member of.
 * Read-only: members manage themselves implicitly through team membership but
 * cannot leave or join teams from this surface — that's an admin action under
 * Settings → Teams.
 */
export const MyTeamsSection: React.FC<MyTeamsSectionProps> = ({ teams }) => {
  const teamManagement = useTeamManagementEntitlement();
  if (!teamManagement || !teams || teams.length === 0) {
    return null;
  }
  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="m-0 p-0 text-lg font-medium text-neutral-900 dark:text-white">Your teams</h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          You are a member of these teams. SHARED items targeted at any of these teams are visible
          to you.
        </p>
      </div>
      <div className="px-4 py-5 sm:px-6">
        <div className="flex flex-wrap gap-2">
          {teams.map((t) => (
            <Badge key={t.id} color="sky">
              {t.name}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
};

// Re-export under the canonical settings shape so the route + index module
// can consume both pieces with a single import.
export { SettingsTeamsPage as TeamsPage };
